import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Comparator, comparatorDefs, SerializedCondition } from "../types";
import Select from "@/components/Select";
import useApi from "@/hooks/useApi";
import VariableTextInput from "@/components/VariableTextInput";
import useSurveys from "@/hooks/useSurveys";
import RegexPatternInput from "./RegexPatternInput";

export type AttributeCondition = {
  attribute: string;
  comparator: Comparator;
  value: string;
};

interface FieldOption {
  name: string;
  label: string;
  scope: "built-in" | "contact" | "conversation" | "workflow" | "survey";
  hint?: string; // e.g. survey name
}

const BUILT_IN_FIELDS: FieldOption[] = [
  { name: "name", label: "Full Name", scope: "built-in" },
  { name: "firstName", label: "First Name", scope: "built-in" },
  { name: "lastName", label: "Last Name", scope: "built-in" },
  { name: "email", label: "Email", scope: "built-in" },
  { name: "phone", label: "Phone", scope: "built-in" },
  { name: "language", label: "Language (browser)", scope: "built-in" }
];

const SCOPE_LABELS: Record<string, string> = {
  "built-in": "Built-in",
  contact: "Contact",
  conversation: "Conversation",
  workflow: "Workflow",
  survey: "Survey Fields"
};

const SCOPE_ORDER = ["built-in", "survey", "contact", "conversation", "workflow"] as const;

export function AttributeEditor({
  condition,
  onUpdate
}: {
  condition: SerializedCondition;
  onUpdate: (condition: SerializedCondition) => void;
}) {
  const attributeCondition = JSON.parse(
    condition.data || "{}"
  ) as AttributeCondition;

  const [fields, setFields] = useState<FieldOption[]>(BUILT_IN_FIELDS);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const api = useApi();
  const { surveys } = useSurveys();

  useEffect(() => {
    async function loadFields() {
      try {
        const res = await api.messaging.get("ContactFieldDefinitions/list");
        const defs: FieldOption[] = [];
        if (res.ok) {
          const data = await res.json();
          const raw = Array.isArray(data)
            ? data
            : Array.isArray(data?.definitions)
              ? data.definitions
              : [];
          raw
            .filter((d: { name?: string; label?: string }) => d.name || d.label)
            .forEach((d: { name?: string; label?: string; scope?: string }) => {
              defs.push({
                name: d.name || d.label?.toLowerCase().replace(/\s+/g, "_") || "",
                label: d.label || d.name || "",
                scope: (d.scope as FieldOption["scope"]) || "conversation"
              });
            });
        }

        // Build survey fields from all saved surveys — deduplicated by variable name
        const surveyFields: FieldOption[] = [];
        const seen = new Set<string>();
        for (const survey of surveys) {
          for (const q of survey.questions ?? []) {
            const varName = q.variable;
            if (!varName || seen.has(varName)) continue;
            // Skip variables that are already in built-ins or contact defs
            if (BUILT_IN_FIELDS.some((f) => f.name === varName)) continue;
            seen.add(varName);
            surveyFields.push({
              name: varName,
              label: q.text || varName,
              scope: "survey",
              hint: survey.name
            });
          }
        }

        setFields([...BUILT_IN_FIELDS, ...surveyFields, ...defs]);
      } catch {
        // keep built-ins only
      }
    }
    loadFields();
  }, [surveys]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  function update(data: AttributeCondition) {
    onUpdate({ ...condition, data: JSON.stringify(data) });
  }

  function selectField(field: FieldOption) {
    update({ ...attributeCondition, attribute: field.name });
    setOpen(false);
    setSearch("");
    setManualMode(false);
  }

  const isKnownField = fields.some((f) => f.name === attributeCondition.attribute);
  const effectiveManual = manualMode || (!!attributeCondition.attribute && !isKnownField);

  // If the selected attribute is a choice/checkbox survey question, surface its options
  const matchedSurveyQuestion = (() => {
    if (!attributeCondition.attribute) return null;
    for (const survey of surveys) {
      const q = (survey.questions ?? []).find(
        (q) =>
          q.variable === attributeCondition.attribute &&
          (q.type === "choice" || q.type === "checkbox")
      );
      if (q) return q;
    }
    return null;
  })();

  const surveyChoiceOptions = matchedSurveyQuestion?.options?.filter((o) => o.trim()) ?? [];

  const currentField = fields.find((f) => f.name === attributeCondition.attribute);
  const displayLabel = currentField
    ? `${currentField.label} (${SCOPE_LABELS[currentField.scope]})`
    : attributeCondition.attribute || "";

  const filtered = search.trim()
    ? fields.filter(
        (f) =>
          f.name.toLowerCase().includes(search.toLowerCase()) ||
          f.label.toLowerCase().includes(search.toLowerCase())
      )
    : fields;

  return (
    <>
      {/* Attribute picker */}
      {effectiveManual ? (
        <div className="flex gap-1 items-center">
          <input
            type="text"
            placeholder="Variable name (e.g. contact.tier)"
            value={attributeCondition.attribute || ""}
            onChange={(e) =>
              update({ ...attributeCondition, attribute: e.target.value })
            }
            className="border rounded-md p-2 flex-1 text-sm"
          />
          <button
            type="button"
            className="text-xs text-blue-600 hover:underline whitespace-nowrap"
            onClick={() => {
              setManualMode(false);
              setOpen(true);
            }}
          >
            Pick field
          </button>
        </div>
      ) : (
        <div ref={containerRef} className="relative">
          <button
            type="button"
            className="w-full text-left border rounded-md p-2 text-sm bg-white hover:border-blue-400 flex items-center justify-between gap-2"
            onClick={() => setOpen(!open)}
          >
            <span className={displayLabel ? "text-slate-800" : "text-slate-400"}>
              {displayLabel || "Select a field or variable..."}
            </span>
            <svg
              className="w-4 h-4 text-slate-400 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {open && (
            <div className="absolute left-0 top-full mt-1 w-full bg-white rounded-lg border border-slate-200 shadow-lg z-50 flex flex-col max-h-72">
              {/* Search */}
              <div className="p-2 border-b border-slate-100">
                <input
                  autoFocus
                  type="text"
                  placeholder="Search fields..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400"
                />
              </div>

              {/* Grouped list */}
              <div className="overflow-y-auto flex-1">
                {filtered.length === 0 && (
                  <div className="p-3 text-sm text-slate-400 text-center">
                    No fields found
                  </div>
                )}
                {SCOPE_ORDER.map((scope) => {
                  const items = filtered.filter((f) => f.scope === scope);
                  if (items.length === 0) return null;
                  return (
                    <div key={scope}>
                      <div className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100">
                        {SCOPE_LABELS[scope]}
                      </div>
                      {items.map((f) => (
                        <button
                          key={f.name}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                          onClick={() => selectField(f)}
                        >
                          <code className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
                            {`<<${f.name}>>`}
                          </code>
                          <span className="text-sm text-slate-700 truncate flex-1">
                            {f.label}
                          </span>
                          {f.hint && (
                            <span className="text-xs text-slate-400 truncate shrink-0 max-w-[100px]" title={f.hint}>
                              {f.hint}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Footer — type manually + create link */}
              <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 rounded-b-lg flex items-center justify-between">
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-blue-600 hover:underline"
                  onClick={() => {
                    setOpen(false);
                    setManualMode(true);
                  }}
                >
                  Type manually...
                </button>
                <Link
                  to="/contact_fields"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline"
                  onClick={() => setOpen(false)}
                >
                  + Create custom field
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Comparator */}
      <Select
        options={comparatorDefs}
        value={attributeCondition.comparator}
        onChange={(v) =>
          update({ ...attributeCondition, comparator: v as Comparator })
        }
      />

      {/* Value — hidden for exists/notExists; regex input for regex comparators; dropdown for choice survey fields */}
      {!["exists", "notExists"].includes(attributeCondition.comparator) && (
        attributeCondition.comparator === "regex" || attributeCondition.comparator === "notRegex" ? (
          <RegexPatternInput
            value={attributeCondition.value || ""}
            onChange={(value) => update({ ...attributeCondition, value })}
            placeholder="e.g. ^\+44|^(en|fr)-"
            hint="Matches the attribute value. Case-insensitive."
            examples={[
              "^\\+44",
              "^(enterprise|business)$",
              "^[a-zA-Z0-9._%+\\-]+@acme\\.com$"
            ]}
          />
        ) : surveyChoiceOptions.length > 0 ? (
          <div className="flex flex-col gap-1">
            <select
              className="border rounded-md p-2 text-sm bg-white"
              value={attributeCondition.value || ""}
              onChange={(e) => update({ ...attributeCondition, value: e.target.value })}
            >
              <option value="">Select an option...</option>
              {surveyChoiceOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <button
              type="button"
              className="text-xs text-slate-400 hover:text-blue-600 text-left"
              onClick={() => update({ ...attributeCondition, value: "" })}
              title="Type a custom value instead"
            >
              or type a custom value...
            </button>
            {/* Allow override if they want to type something not in the list */}
            {attributeCondition.value && !surveyChoiceOptions.includes(attributeCondition.value) && (
              <VariableTextInput
                placeholder="Custom value"
                value={attributeCondition.value}
                onChange={(value) => update({ ...attributeCondition, value })}
              />
            )}
          </div>
        ) : (
          <VariableTextInput
            placeholder='Value (type @ to insert a variable)'
            value={attributeCondition.value || ""}
            onChange={(value) => update({ ...attributeCondition, value })}
          />
        )
      )}
    </>
  );
}
